import { useEffect, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import {
  HiMinus,
  HiPlus,
  HiOutlineTrash,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
} from 'react-icons/hi';
import Segment from '../../../ui/Segment';
import OptionPill from './OptionPill';
import AddNoteArea from '../../../shared/AddNoteArea';
import getPrices from '../../../../utils/getPrices';
import { sumUnitQty, optionEntryId } from '../../../../utils/optionQty';
import {
  buildOptionGroupSchemas,
  buildOptionGroupInitialValues,
} from '../../../../utils/optionGroupForm';
import { getNumberFormat } from '../../../../utils/getNumberFormat';
import { mainOptionKcalLabel } from '../../../../utils/calories';
import { getVisibleItems } from '../../../../utils/getDisplayItems';
import {
  calculateBXGYQuantity,
  calculateBXGYPrices,
  calculateFreeItemPrices,
  getBXGYDisplayInfo, // eslint-disable-line no-unused-vars
  isBXGYCoupon,
} from '../../../../utils/coupon';
import {
  getBuyItemCount,
  getGetItemCount,
} from '../../../../utils/coupon/types';

function QtyMinSync({ minQty, qty, setFieldValue }) {
  useEffect(() => {
    if (typeof minQty !== 'number') return;
    if (qty < minQty) setFieldValue('qty', minQty);
  }, [minQty, qty, setFieldValue]);
  return null;
}

// The header kcal lives outside <Formik> (ProductTitleBlock is a sibling of
// this form), so the label is published upward from inside the render prop.
// A null-rendering effect component is the only place a hook can legally run
// here — the render prop itself is a plain function, not a component.
function KcalPublisher({ label, onKcalChange }) {
  useEffect(() => {
    onKcalChange?.(label);
  }, [label, onKcalChange]);
  return null;
}

/**
 * Redesigned product form for the new UI.
 * - Keeps every Formik field, validation rule, and helper that the
 *   legacy ProductForm relies on (BXGY, free item, locked item, etc.).
 * - Rebuilds only the visual layer with new tokens + modern pills.
 */
const ProductForm = ({
  onProductNoteChange,
  productNote,
  product,
  basketProduct,
  onSubmit,
  onDelete,
  lockedItemId,
  coupon,
  onAddBXGYItem, // eslint-disable-line no-unused-vars
  onKcalChange,
}) => {
  const { customernoteactive } = useSelector(
    (state) => state.base.common.currentStore,
  );
  const [isNoteOpen, setIsNoteOpen] = useState(true);

  const optionGroups = (product.optionGroups || [])
    .slice()
    .sort((a, b) => a.displayorder - b.displayorder);
  // When the product has only a single variant there's nothing to pick —
  // hide the "Select size" block (the value is pre-selected anyway).
  const singleItem = (product.items || []).length === 1;
  const dynamicValidationSchema = Yup.object().shape({
    items: Yup.array()
      .min(1, 'Please select main option.')
      .max(1, 'You can select only 1.'),
    ...(optionGroups.length > 0 && buildOptionGroupSchemas(optionGroups)),
  });

  const getInfoText = (min, max) => {
    if (min > 0 && min === max) return `Select ${min}`;
    if (min === 0 && max > 0) return `Choose up to ${max}`;
    if (max === 0) return '';
    return `Choose ${min} to ${max}`;
  };

  const isBXGY = isBXGYCoupon(coupon);
  let initialQty = basketProduct?.qty || 1;
  if (isBXGY && !basketProduct && coupon) {
    initialQty = getBuyItemCount(coupon) + getGetItemCount(coupon);
  }

  return (
    <Formik
      enableReinitialize
      initialValues={{
        qty: initialQty,
        items: basketProduct
          ? [basketProduct.item.id]
          : lockedItemId
            ? [lockedItemId]
            : (getVisibleItems(product.items)[0] || product.items[0])
              ? [(getVisibleItems(product.items)[0] || product.items[0])?.id]
              : [],
        ...(optionGroups.length > 0 &&
          buildOptionGroupInitialValues(optionGroups, basketProduct)),
      }}
      validationSchema={dynamicValidationSchema}
      validateOnMount
      onSubmit={(values) => onSubmit(values)}
    >
      {({ values, errors, setFieldValue, isValid }) => {
        const isFreeItem =
          lockedItemId && lockedItemId === values.items[0];
        const bxgyResult = isBXGY
          ? calculateBXGYQuantity(coupon, values.qty)
          : null;
        const minQty = 1;

        const pricesInfo = getPrices({ ...values, qty: 1 }, product);
        const itemPrice = pricesInfo.foundItem?.price || 0;
        const optionsPricePerItem = pricesInfo.refinedOptionList.reduce(
          (sum, opt) => sum + (opt.price || 0) * (opt.qty ?? 1),
          0,
        );

        // Selected main option, resolved from product.items (the real object
        // with showcalories), keyed by the selected item id.
        const selectedMainItem = (product.items || []).find(
          (it) => it.id === (pricesInfo.foundItem?.id ?? values.items?.[0]),
        );

        // Header kcal = selected main option only. Option accumulation hidden
        // 2026-07-27 (main-option-only scope-down). Re-enable:
        // selectionKcalLabel(selectedMainItem, resolveSelectedOptions(values, optionGroups)).
        const headerKcalLabel = mainOptionKcalLabel(selectedMainItem);

        let baseTotal;
        let displayTotal;
        if (isFreeItem) {
          const r = calculateFreeItemPrices({
            itemPrice,
            optionsPricePerItem,
            qty: values.qty,
          });
          displayTotal = r.displayTotal;
          baseTotal = r.originalTotal;
        } else if (isBXGY && bxgyResult?.canApply) {
          const r = calculateBXGYPrices({
            coupon,
            qty: values.qty,
            itemPrice,
            optionsPricePerItem,
          });
          displayTotal = r.displayTotal;
          baseTotal = r.originalTotal;
        } else {
          const p = getPrices(values, product);
          baseTotal = p.total;
          displayTotal = baseTotal;
        }

        const showDiscountSplit =
          isFreeItem || (isBXGY && bxgyResult?.canApply);

        return (
          <Form className="flex flex-col h-full min-h-0">
            <QtyMinSync
              minQty={minQty}
              qty={values.qty}
              setFieldValue={setFieldValue}
            />
            <KcalPublisher label={headerKcalLabel} onKcalChange={onKcalChange} />

            <div
              className="flex flex-col gap-5 flex-1 min-h-0 overflow-y-auto pm-form-scroll"
            >
              <style>{`
                .pm-form-scroll {
                  scrollbar-width: none;
                  -ms-overflow-style: none;
                }
                .pm-form-scroll::-webkit-scrollbar { display: none; }
              `}</style>
              {/* Main Option / Variants — hidden when there's only one */}
              {!singleItem && (
              <FieldBlock
                label="Select size"
                required
                satisfied={
                  Array.isArray(values.items) && values.items.length > 0
                }
                error={errors.items}
              >
                <Field name="items">
                  {({ field, form }) => (
                    <Segment
                      className="w-full"
                      selectionType="single"
                      value={values.items}
                      onChange={(val) => {
                        if (lockedItemId && val[0] !== lockedItemId) return;
                        form.setFieldValue(field.name, val);
                      }}
                    >
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-[10px]">
                        {product.items.map((item) => {
                          const isLocked = lockedItemId === item.id;
                          const soldOut = item.visible === false;
                          return (
                            <Segment.Item
                              value={item.id}
                              key={item.id}
                              disabled={soldOut}
                            >
                              {({
                                ref,
                                active,
                                onSegmentItemClick,
                                disabled,
                              }) => (
                                <OptionPill
                                  ref={ref}
                                  active={active}
                                  onClick={
                                    isLocked || soldOut
                                      ? undefined
                                      : onSegmentItemClick
                                  }
                                  disabled={isLocked || disabled}
                                  soldOut={soldOut}
                                  label={
                                    item.name.length > 28
                                      ? item.name.slice(0, 28) + '…'
                                      : item.name
                                  }
                                  price={item.price}
                                  kcal={mainOptionKcalLabel(item)}
                                />
                              )}
                            </Segment.Item>
                          );
                        })}
                      </div>
                    </Segment>
                  )}
                </Field>
              </FieldBlock>
              )}

              {/* Option Groups */}
              {optionGroups.map((optionGroup) => {
                  const options = optionGroup.options;
                  const groupValue = values[optionGroup.id] || [];
                  const { min, max, allowquantities } = optionGroup;
                  // Unit count across the group. For flag-off string[] values
                  // this equals groupValue.length, so today's math is intact.
                  const groupUnits = sumUnitQty(groupValue);
                  const atMax = max > 0 && groupUnits >= max;
                  const isSelected = (id) =>
                    groupValue.some((entry) => optionEntryId(entry) === id);
                  // Quantity groups count up in the header once something is
                  // picked ("2 of 3"); before that the requirement text stands.
                  const statusText =
                    allowquantities && groupUnits > 0 && max > 0
                      ? `${groupUnits} of ${max}`
                      : getInfoText(min, max);
                  const groupDescription = (optionGroup.description || '').trim();
                  const hint = allowquantities
                    ? groupDescription || 'Tap + to add more of the same option.'
                    : groupDescription;
                  return (
                    <FieldBlock
                      key={optionGroup.id}
                      label={optionGroup.name}
                      required={min > 0}
                      satisfied={min > 0 && groupUnits >= min}
                      description={statusText}
                      hint={hint}
                      error={errors[optionGroup.id]}
                    >
                      <Field name={optionGroup.id}>
                        {({ field, form }) => (
                          <Segment
                            className="w-full"
                            selectionType={
                              allowquantities
                                ? 'quantity'
                                : max > 1
                                  ? 'multiple'
                                  : 'single'
                            }
                            value={values[optionGroup.id]}
                            onChange={(val) =>
                              form.setFieldValue(field.name, val)
                            }
                          >
                            <div className="flex flex-col sm:grid sm:grid-cols-3 gap-2">
                              {options.map((option) => {
                                const soldOut = option.visible === false;
                                return (
                                <Segment.Item
                                  value={option.id}
                                  key={option.id}
                                  disabled={
                                    soldOut ||
                                    (atMax && !isSelected(option.id))
                                  }
                                  incrementDisabled={atMax}
                                >
                                  {({
                                    ref,
                                    active,
                                    qty,
                                    onSegmentItemClick,
                                    onSegmentItemDecrement,
                                    disabled,
                                  }) => (
                                    <OptionPill
                                      ref={ref}
                                      active={active}
                                      qty={allowquantities ? qty : 0}
                                      onClick={onSegmentItemClick}
                                      onIncrement={
                                        allowquantities && active
                                          ? onSegmentItemClick
                                          : undefined
                                      }
                                      incrementDisabled={atMax}
                                      onDecrement={
                                        allowquantities && active
                                          ? onSegmentItemDecrement
                                          : undefined
                                      }
                                      disabled={disabled}
                                      soldOut={soldOut}
                                      label={option.name}
                                      price={option.price}
                                      // Per-modifier kcal hidden 2026-07-27
                                      // (main-option-only scope-down).
                                      kcal={undefined}
                                    />
                                  )}
                                </Segment.Item>
                                );
                              })}
                            </div>
                          </Segment>
                        )}
                      </Field>
                    </FieldBlock>
                  );
                })}

              {/* Special Instructions */}
              {customernoteactive && (
                <div>
                  <button
                    type="button"
                    onClick={() => setIsNoteOpen(!isNoteOpen)}
                    className="w-full flex items-center justify-between"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      fontFamily: 'inherit',
                      cursor: 'pointer',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '14px',
                        fontWeight: 700,
                        color: 'var(--ui-ink)',
                      }}
                    >
                      Special instructions
                    </span>
                    {isNoteOpen ? (
                      <HiOutlineChevronUp
                        size={18}
                        style={{ color: 'var(--ui-ink-2)' }}
                      />
                    ) : (
                      <HiOutlineChevronDown
                        size={18}
                        style={{ color: 'var(--ui-ink-2)' }}
                      />
                    )}
                  </button>
                  {isNoteOpen && (
                    <div className="mt-3">
                      <AddNoteArea
                        value={productNote}
                        onChange={(note) => onProductNoteChange(note)}
                        hideTitle={true}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Fixed footer — always visible, flex-shrink: 0 */}
            <div
              className="pm-form-footer"
              style={{
                flexShrink: 0,
                background: 'var(--ui-surface)',
                paddingBottom: 'max(var(--sab), 8px)',
                marginTop: '12px',
              }}
            >
              <div
                className="pt-4"
                style={{ borderTop: '1px solid var(--ui-line)' }}
              >
                <div className="flex items-center justify-between gap-3">
                  {/* Qty stepper */}
                  <div
                    className="inline-flex items-center rounded-[12px] overflow-hidden"
                    style={{
                      background: 'var(--ui-surface-2)',
                      border: '1px solid var(--ui-line)',
                    }}
                  >
                    <button
                      type="button"
                      disabled={values.qty <= minQty}
                      onClick={() =>
                        setFieldValue(
                          'qty',
                          Math.max(minQty, values.qty - 1),
                        )
                      }
                      className="grid place-items-center transition-colors"
                      style={{
                        width: '40px',
                        height: '40px',
                        background: 'transparent',
                        border: 'none',
                        color:
                          values.qty <= minQty
                            ? 'var(--ui-ink-3)'
                            : 'var(--ui-ink)',
                        cursor:
                          values.qty <= minQty ? 'not-allowed' : 'pointer',
                      }}
                      aria-label="Decrease quantity"
                    >
                      <HiMinus size={16} />
                    </button>
                    <span
                      className="font-extrabold text-center"
                      style={{
                        fontSize: '16px',
                        minWidth: '32px',
                        color: 'var(--ui-ink)',
                      }}
                    >
                      {values.qty}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setFieldValue('qty', values.qty + 1)
                      }
                      className="grid place-items-center transition-colors"
                      style={{
                        width: '40px',
                        height: '40px',
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--ui-ink)',
                        cursor: 'pointer',
                      }}
                      aria-label="Increase quantity"
                    >
                      <HiPlus size={16} />
                    </button>
                  </div>

                  {/* Total */}
                  <div className="flex flex-col items-end flex-1 min-w-0 mx-2">
                    {showDiscountSplit ? (
                      <>
                        <span
                          style={{
                            fontSize: '10.5px',
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: 'var(--ui-ink-3)',
                          }}
                        >
                          Item total
                        </span>
                        <span
                          style={{
                            fontFamily: 'Geist Mono, monospace',
                            fontSize: '22px',
                            fontWeight: 800,
                            color: 'var(--ui-green)',
                            lineHeight: 1,
                            letterSpacing: '-0.01em',
                          }}
                        >
                          ${getNumberFormat(displayTotal)}
                        </span>
                        <span
                          style={{
                            fontSize: '12px',
                            color: 'var(--ui-ink-3)',
                            textDecoration: 'line-through',
                          }}
                        >
                          ${getNumberFormat(baseTotal)}
                        </span>
                      </>
                    ) : (
                      <>
                        <span
                          style={{
                            fontSize: '10.5px',
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: 'var(--ui-ink-3)',
                          }}
                        >
                          Item total
                        </span>
                        <span
                          style={{
                            fontFamily: 'Geist Mono, monospace',
                            fontSize: '22px',
                            fontWeight: 800,
                            color: 'var(--ui-ink)',
                            lineHeight: 1,
                            letterSpacing: '-0.01em',
                          }}
                        >
                          ${getNumberFormat(displayTotal)}
                        </span>
                      </>
                    )}
                    {isBXGY &&
                      coupon &&
                      (() => {
                        const need = getBuyItemCount(coupon) + 1;
                        if (values.qty < need) {
                          const more = need - values.qty;
                          return (
                            <span
                              className="mt-1"
                              style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                color: '#B91C1C',
                              }}
                            >
                              Add {more} more to get 1 free
                            </span>
                          );
                        }
                        return null;
                      })()}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {basketProduct && (
                      <button
                        type="button"
                        onClick={() => onDelete()}
                        aria-label="Remove"
                        className="grid place-items-center transition-colors"
                        style={{
                          width: '44px',
                          height: '44px',
                          borderRadius: '12px',
                          background: 'transparent',
                          border: '1px solid var(--ui-line-2)',
                          color: '#DC2626',
                          cursor: 'pointer',
                        }}
                      >
                        <HiOutlineTrash size={18} />
                      </button>
                    )}
                    <button
                      type="submit"
                      disabled={!isValid}
                      className="font-bold transition-all"
                      style={{
                        padding: '0 22px',
                        height: '44px',
                        fontSize: '14px',
                        borderRadius: '12px',
                        background: isValid
                          ? 'var(--accent, var(--site-color))'
                          : 'var(--ui-surface-2)',
                        color: isValid
                          ? 'var(--accent-fg, #FFFFFF)'
                          : 'var(--ui-ink-3)',
                        border: 'none',
                        cursor: isValid ? 'pointer' : 'not-allowed',
                        whiteSpace: 'nowrap',
                        boxShadow: isValid
                          ? '0 8px 20px -4px var(--accent-soft, var(--site-color-10)), 0 2px 4px rgba(15, 23, 42, 0.08)'
                          : 'none',
                      }}
                      onMouseEnter={(e) => {
                        if (isValid) e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      {basketProduct ? 'Update' : 'Add to cart'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

// ============= Sub components =============

const FieldBlock = ({
  label,
  required,
  satisfied,
  description,
  hint,
  error,
  children,
}) => (
  <div>
    <div
      className={`flex items-baseline justify-between ${hint ? 'mb-[4px]' : 'mb-[10px]'}`}
    >
      <div className="flex items-baseline gap-[6px]">
        <span
          style={{
            fontSize: '13.5px',
            fontWeight: 700,
            color: 'var(--ui-ink)',
            letterSpacing: '-0.01em',
          }}
        >
          {label}
        </span>
        {required && (
          <span
            style={{
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              padding: '1px 7px',
              borderRadius: '100px',
              background: satisfied
                ? 'rgba(34, 197, 94, 0.12)'
                : 'var(--accent-soft, var(--site-color-10))',
              color: satisfied
                ? 'var(--action-green-hover, #16a34a)'
                : 'var(--accent, var(--site-color))',
              transition: 'background 0.2s, color 0.2s',
            }}
          >
            {satisfied ? '✓ Done' : 'Required'}
          </span>
        )}
      </div>
      {description && (
        <span style={{ fontSize: '11.5px', color: 'var(--ui-ink-2)' }}>
          {description}
        </span>
      )}
    </div>
    {hint && (
      <p
        className="mb-[10px]"
        style={{ fontSize: '12px', lineHeight: 1.4, color: 'var(--ui-ink-2)' }}
      >
        {hint}
      </p>
    )}
    {children}
    {error && typeof error === 'string' && (
      <div
        className="mt-2"
        style={{ fontSize: '11.5px', color: '#DC2626' }}
      >
        {error}
      </div>
    )}
  </div>
);


export default ProductForm;
